import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { deviceName } from '../audio/devices';
import { useHeadset, useSystemDefaultDeviceNames } from '../audio/headset';
import { SpeakerPicker } from './SpeakerPicker';
import styles from './AudioMenu.module.css';

const iconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

function HeadphonesIcon({ size = 20 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size} viewBox="0 0 24 24">
      <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
      <rect x={3} y={14} width={4.5} height={7} rx={1.5} />
      <rect x={16.5} y={14} width={4.5} height={7} rx={1.5} />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg {...iconProps} width={16} height={16} viewBox="0 0 24 24">
      <rect x={9} y={3} width={6} height={11} rx={3} />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...iconProps} width={16} height={16} viewBox="0 0 24 24">
      <circle cx={12} cy={12} r={8.5} />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg {...iconProps} width={18} height={18} viewBox="0 0 24 24">
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
    </svg>
  );
}

/**
 * The header's sound settings: a headphones button with a tick (the headset check is valid) or a
 * cross (it is not), opening a panel with two parts. Practice: the checked output and microphone
 * and the measured delay, changed only through the headset check (the delay and the leak test hold
 * for those devices). Rehearsal: the room's speakers, kept in this browser. Escape or a click
 * elsewhere closes it.
 */
export function AudioMenu() {
  const { t } = useTranslation();
  const location = useLocation();
  const headset = useHeadset();
  const defaults = useSystemDefaultDeviceNames();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const settings = headset.settings;
  const status = settings ? t('nav.headsetChecked') : t('nav.headsetNeeded');

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('a, button, select')?.focus();
    const outside = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  // Close when the page changes (e.g. after following the link to the headset check).
  useEffect(() => setOpen(false), [location.pathname]);

  const explicitOutput = settings?.outputDeviceId ? deviceName(settings.outputLabel) : null;
  const outputText = explicitOutput ?? (defaults.output ? t('practice.systemDefaultNamed', { name: defaults.output }) : t('practice.systemDefault'));
  const inputText = (settings?.inputLabel ? deviceName(settings.inputLabel) : null) ?? defaults.input ?? t('practice.systemDefault');

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.button}
        aria-expanded={open}
        aria-controls="audio-menu"
        title={`${t('audio.open')} · ${status}`}
        onClick={() => setOpen((o) => !o)}
        data-testid="audio-menu-button"
      >
        <HeadphonesIcon />
        <span className={`${styles.badge} ${settings ? styles.ok : styles.needed}`} aria-hidden="true">
          {settings ? (
            <svg width={9} height={9} viewBox="0 0 10 10">
              <path d="M2 5.2 4.2 7.3 8 3" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width={9} height={9} viewBox="0 0 10 10">
              <path d="M2.8 2.8l4.4 4.4M7.2 2.8 2.8 7.2" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
            </svg>
          )}
        </span>
        <span className={styles.srOnly} data-testid="headset-status">
          {status}
        </span>
      </button>
      {open && (
        <div
          id="audio-menu"
          ref={panelRef}
          role="dialog"
          aria-label={t('audio.title')}
          className={styles.panel}
          data-testid="audio-menu"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
              buttonRef.current?.focus();
            }
          }}
        >
          <section className={styles.section} aria-labelledby="audio-practice">
            <div className={styles.sectionHead}>
              <HeadphonesIcon size={18} />
              <h2 id="audio-practice" className={styles.sectionTitle}>
                {t('audio.practice')}
              </h2>
              <span className={`${styles.status} ${settings ? styles.statusOk : styles.statusNeeded}`}>
                {settings ? t('audio.checked') : t('audio.notChecked')}
              </span>
            </div>
            {settings ? (
              <ul className={styles.devices} data-testid="devices">
                <li title={t('practice.output', { name: outputText })} data-testid="device-output">
                  <HeadphonesIcon size={16} />
                  <span>{outputText}</span>
                </li>
                <li title={t('practice.input', { name: inputText })} data-testid="device-input">
                  <MicIcon />
                  <span>{inputText}</span>
                </li>
                <li data-testid="device-latency">
                  <ClockIcon />
                  <span>{t('audio.latency', { ms: settings.latencyMs })}</span>
                </li>
              </ul>
            ) : (
              <p className={styles.note}>{t('audio.needCheck')}</p>
            )}
            <Link
              to="/headset"
              state={{ returnTo: location.pathname, openDevices: !!settings }}
              className={styles.action}
              onClick={() => setOpen(false)}
            >
              {settings ? t('practice.changeDevices') : t('nav.checkHeadset')}
            </Link>
          </section>
          <section className={styles.section} aria-labelledby="audio-rehearsal">
            <div className={styles.sectionHead}>
              <SpeakerIcon />
              <h2 id="audio-rehearsal" className={styles.sectionTitle}>
                {t('audio.rehearsal')}
              </h2>
            </div>
            <SpeakerPicker />
          </section>
        </div>
      )}
    </div>
  );
}
